<template>
  <!-- Fixed AI Summary Panel -->
  <div v-if="isVisible" class="ai-summary-panel">
    <button @click="$emit('close')" class="ai-summary-panel__close-btn">&times;</button>

    <div class="ai-summary-panel__content">
      <div class="ai-summary-raw">
        <pre>{{ displayContent }}</pre>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
const _props = withDefaults(
  defineProps<{
    isVisible: boolean;
    content?: string | null;
  }>(),
  {
    isVisible: false,
    content: null,
  }
);

const _emit = defineEmits<{
  (e: "close"): void;
}>();

const displayContent = computed(() => {
  let s = (_props.content ?? "").trim();
  if (!s) return "";

  if (s.startsWith("{")) {
    s = s.slice(1);
    if (s.startsWith("\r\n")) s = s.slice(2);
    else if (s.startsWith("\n")) s = s.slice(1);
  }

  if (s.endsWith("}")) {
    s = s.slice(0, -1);
    if (s.endsWith("\r\n")) s = s.slice(0, -2);
    else if (s.endsWith("\n")) s = s.slice(0, -1);
  }

  return s.trim();
});
/* Previous, more structured format
// Format label for display ===================================================
const formatLabel = (key: string): string => {
  return key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, str => str.toUpperCase())
    .trim();
};
*/
</script>

<style scoped lang="scss">
// AI Summary Panel ===========================================================
.ai-summary-panel {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  max-height: 60vh;
  overflow-y: auto;

  &__close-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: rgba(0, 0, 0, 0.6);
    z-index: 1001;

    &:hover {
      background: rgba(0, 0, 0, 0.1);
      color: rgba(0, 0, 0, 0.8);
    }
  }

  &__content {
    padding: 20px;
    padding-right: 60px; // Make room for close button
  }
}

// AI Summary Items ===========================================================
.ai-summary-item {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  &__summary {
    color: rgba(0, 0, 0, 0.8);
    font: 16px/24px sans-serif;
    line-height: 1.5;
    margin-bottom: 20px;
  }

  &__inline {
    display: flex;
    gap: 8px;
    align-items: flex-start;
  }

  &__label {
    color: rgba(0, 0, 0, 0.8);
    font: 600 14px/20px sans-serif;
    min-width: 100px;
    flex-shrink: 0;
  }

  &__value {
    color: rgba(91, 91, 91, 1);
    font: 14px/20px sans-serif;
    flex: 1;
  }
}

// AI Summary Raw =============================================================
.ai-summary-raw {
  pre {
    background: rgba(248, 249, 250, 1);
    border: 1px rgba(224, 227, 231, 1) solid;
    border-radius: 6px;
    padding: 16px;
    font:
      14px/20px "SF Mono",
      Monaco,
      Consolas,
      "Liberation Mono",
      "Courier New",
      monospace;
    color: rgba(36, 41, 47, 1);
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-x: auto;
    margin: 0;
  }
}
</style>
